import {
  Button,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Alert,
  SvgIcon,
  Typography,
} from '@mui/material';
import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { ListPageTemplate } from '../../../components/page-templates/list-page/ListPageTemplate';
import BackButton from '../../../components/shared/actions/BackButton';
import ProductsListPageContent from './components/ProductsListPageContent';
import { UserContext } from '../../../components/shared/useUser';
import { Plus, Download01, ArrowUp } from '../../../assets';
import { client, MarketItemUploadRequest, MarketItemUploadResult } from '../../../app/services/api/orderManagementClient';
import useLogError from '../../../hooks/useLogError';
import { useInstanceMarket } from '../../../contexts/InstanceMarketContext';

const ProductManagementPage = () => {
  const { t } = useTranslation('productManagement');
  const navigate = useNavigate();
  const location = useLocation();
  const { logError } = useLogError();

  const { hasPermissionTo } = useContext(UserContext);
  const [hasPermissionToCreateProduct, setHasPermissionToCreateProduct] = useState<boolean>(false);

  const [currentFilterInstance, setCurrentFilterInstance] = useState<string>('');
  const [currentFilterMarket, setCurrentFilterMarket] = useState<string>('');
  const [currentFilterSearchTerm, setCurrentFilterSearchTerm] = useState<string>('');
  const [currentFilterSortColumn, setCurrentFilterSortColumn] = useState<string>('');
  const [currentFilterSortDirection, setCurrentFilterSortDirection] = useState<'asc' | 'desc'>('asc');
  const [currentFilterPage, setCurrentFilterPage] = useState<number>(0);

  const [showDownloadDialog, setShowDownloadDialog] = useState<boolean>(false);
  const [showUploadDialog, setShowUploadDialog] = useState<boolean>(false);
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [selectedMarket, setSelectedMarket] = useState<string>('');
  const { instances, getMarkets, ensureMarketsLoaded, defaultInstanceId, defaultMarketId, defaultFiltersLoading } = useInstanceMarket();
  const markets = getMarkets(selectedInstance);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadResult, setUploadResult] = useState<MarketItemUploadResult | null>(null);

  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [messageSeverity, setMessageSeverity] = useState<'success' | 'error' | 'info'>('success');

  const { selectedStore } = useContext(UserContext);

  useEffect(() => {
    setHasPermissionToCreateProduct(hasPermissionTo(['ProductCreate', 'ConfigurationAdmin']));
  }, [hasPermissionTo]);

  useEffect(() => {
    setHasPermissionToCreateProduct(hasPermissionTo(['ProductCreate', 'ConfigurationAdmin']));

    // Initialize filter state from URL parameters
    const searchParams = new URLSearchParams(location.search);
    const companyIdParam = searchParams.get('companyId');
    const customerTypeIdParam = searchParams.get('customerTypeId');
    const searchTermParam = searchParams.get('searchTerm');
    const sortParam = searchParams.get('sort');
    const directionParam = searchParams.get('direction');
    const pageParam = searchParams.get('page');

    if (companyIdParam) setCurrentFilterInstance(companyIdParam);
    if (customerTypeIdParam) setCurrentFilterMarket(customerTypeIdParam);
    if (searchTermParam) setCurrentFilterSearchTerm(searchTermParam);
    if (sortParam) setCurrentFilterSortColumn(sortParam);
    if (directionParam === 'asc' || directionParam === 'desc') setCurrentFilterSortDirection(directionParam);
    if (pageParam) setCurrentFilterPage(Number(pageParam) || 0);
  }, [hasPermissionTo, location.search]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (!searchParams.get('companyId') && !searchParams.get('customerTypeId') && !defaultFiltersLoading && defaultInstanceId && defaultMarketId) {
      setCurrentFilterInstance(defaultInstanceId);
      setCurrentFilterMarket(defaultMarketId);
      const params = new URLSearchParams();
      params.set('companyId', defaultInstanceId);
      params.set('customerTypeId', defaultMarketId);
      navigate(`?${params.toString()}`, { replace: true });
    }
  }, [selectedStore, defaultFiltersLoading, defaultInstanceId, defaultMarketId]);

  useEffect(() => {
    if (selectedInstance) {
      ensureMarketsLoaded(selectedInstance);
    } else {
      setSelectedMarket('');
    }
  }, [selectedInstance, ensureMarketsLoaded]);

  const handleCreateNew = () => {
    const params = new URLSearchParams();
    if (currentFilterInstance) params.set('companyId', currentFilterInstance);
    if (currentFilterMarket) params.set('customerTypeId', currentFilterMarket);
    if (currentFilterSearchTerm) params.set('searchTerm', currentFilterSearchTerm);

    const queryString = params.toString();
    navigate(`/procurement/products/create${queryString ? '?' + queryString : ''}`);
  };

  const onCreateNew = () => {
    handleCreateNew();
  };

  const handleFilterChange = (
    instance: string,
    market: string,
    searchTerm?: string,
    sortColumn?: string,
    sortDirection?: 'asc' | 'desc',
    page?: number,
  ) => {
    setCurrentFilterInstance(instance);
    setCurrentFilterMarket(market);
    const newSearchTerm = searchTerm !== undefined ? searchTerm : currentFilterSearchTerm;
    setCurrentFilterSearchTerm(newSearchTerm);
    const newSortColumn = sortColumn !== undefined ? sortColumn : currentFilterSortColumn;
    setCurrentFilterSortColumn(newSortColumn);
    const newSortDirection = sortDirection !== undefined ? sortDirection : currentFilterSortDirection;
    setCurrentFilterSortDirection(newSortDirection);
    const newPage = page !== undefined ? page : currentFilterPage;
    setCurrentFilterPage(newPage);

    const params = new URLSearchParams();
    if (instance) params.set('companyId', instance);
    if (market) params.set('customerTypeId', market);
    if (newSearchTerm) params.set('searchTerm', newSearchTerm);
    if (newSortColumn) params.set('sort', newSortColumn);
    if (newSortDirection) params.set('direction', newSortDirection);
    if (newPage) params.set('page', newPage.toString());
    navigate(`?${params.toString()}`, { replace: true });
  };

  const handleDownloadClick = () => {
    if (currentFilterInstance && currentFilterMarket) {
      setMessage(t('downloadStarting'));
      setMessageSeverity('info');
      setShowMessage(true);
      handleDownload(currentFilterInstance, currentFilterMarket);
    } else {
      setSelectedInstance('');
      setSelectedMarket('');
      setShowDownloadDialog(true);
    }
  };

  const handleUploadClick = () => {
    setSelectedInstance(currentFilterInstance || '');
    setSelectedMarket(currentFilterMarket || '');
    setUploadFile(null);
    setUploadResult(null);
    setShowUploadDialog(true);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    setSelectedInstance(event.target.value);
    setSelectedMarket('');
  };

  const handleMarketChange = (event: SelectChangeEvent<string>) => {
    setSelectedMarket(event.target.value);
  };

  const handleDownload = async (instanceId?: string, marketId?: string) => {
    const downloadInstance = instanceId || selectedInstance;
    const downloadMarket = marketId || selectedMarket;

    if (!downloadInstance || !downloadMarket) {
      setMessage('Please select both Instance and Market to download products');
      setMessageSeverity('error');
      setShowMessage(true);
      return;
    }

    setIsDownloading(true);

    if (!instanceId && !marketId) {
      setMessage(t('downloadStarting'));
      setMessageSeverity('info');
      setShowMessage(true);
    }

    try {
      const response = await client.downloadMarketItems(downloadInstance, downloadMarket);

      if (response.success && response.data) {
        const byteCharacters = atob(response.data.fileData);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.fileName || 'products.xlsx';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        setMessage(t('downloadSuccessMessage'));
        setMessageSeverity('success');
        setShowMessage(true);
        setShowDownloadDialog(false);
      } else {
        setMessage(response.message || t('downloadErrorMessage'));
        setMessageSeverity('error');
        setShowMessage(true);
      }
    } catch (error) {
      logError('Failed to download products');
      setMessage(t('downloadErrorMessage'));
      setMessageSeverity('error');
      setShowMessage(true);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUploadFile(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !selectedInstance || !selectedMarket) {
      setMessage('Please select a file and ensure Instance and Market are selected');
      setMessageSeverity('error');
      setShowMessage(true);
      return;
    }

    setIsUploading(true);
    try {
      const fileBase64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(uploadFile);
      });

      const uploadRequest: MarketItemUploadRequest = {
        companyId: parseInt(selectedInstance),
        customerTypeId: parseInt(selectedMarket),
        fileBase64: fileBase64,
      };

      const response = await client.uploadMarketItems(uploadRequest);

      if (response.success && response.data) {
        setUploadResult(response.data);
        setMessage(t('uploadSuccessMessage'));
        setMessageSeverity('success');
        setShowMessage(true);
      } else {
        setMessage(response.message || t('uploadErrorMessage'));
        setMessageSeverity('error');
        setShowMessage(true);
      }
    } catch (error) {
      logError('Failed to upload products');
      setMessage(t('uploadErrorMessage'));
      setMessageSeverity('error');
      setShowMessage(true);
    } finally {
      setIsUploading(false);
    }
  };

  const handleCloseMessage = () => {
    setShowMessage(false);
  };

  const canViewProducts = hasPermissionTo(['ProductView', 'ConfigurationAdmin']);
  const canExportProducts = hasPermissionTo(['ProductExport', 'ConfigurationAdmin']);
  const canImportProducts = hasPermissionTo(['ProductImport', 'ConfigurationAdmin']);

  if (!canViewProducts) {
    return null;
  }

  return (
    <>
      <ProductsListPageContent
        onCreateNew={onCreateNew}
        onFilterChange={handleFilterChange}
        initialInstance={currentFilterInstance}
        initialMarket={currentFilterMarket}
        initialSearchTerm={currentFilterSearchTerm}
        initialSortColumn={currentFilterSortColumn}
        initialSortDirection={currentFilterSortDirection}
        initialPage={currentFilterPage}
      >
        {({ filterBar, headlineContent, table, snackbar, isInitialLoading }) => (
          <ListPageTemplate
            title={t('products')}
            subtitle={t('subtitle')}
            loading={isInitialLoading}
            loadingLabel={t('loading')}
            backButton={(
              <BackButton
                handleClick={() => navigate('/settings')}
                title={t('backToSettings', 'Back to Settings')}
                isLink={false}
              />
            )}
            actions={(
              <>
                {canExportProducts && (
                  <Button
                    variant="secondary"
                    size="lg"
                    startIcon={<SvgIcon><Download01 /></SvgIcon>}
                    onClick={handleDownloadClick}
                    disabled={isDownloading}
                  >
                    {isDownloading ? t('downloading') : t('download')}
                  </Button>
                )}
                {canImportProducts && (
                  <Button
                    variant="secondary"
                    size="lg"
                    startIcon={<SvgIcon><ArrowUp /></SvgIcon>}
                    onClick={handleUploadClick}
                    disabled={isDownloading || isUploading}
                  >
                    {isUploading ? t('uploading') : t('upload')}
                  </Button>
                )}
                {hasPermissionToCreateProduct && (
                  <Button
                    variant="primary"
                    size="lg"
                    startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}
                    onClick={handleCreateNew}
                    disabled={isDownloading || isUploading}
                  >
                    {t('createNew')}
                  </Button>
                )}
              </>
            )}
            filterContent={filterBar}
            headlineContent={headlineContent}
            table={table}
          >
            {snackbar}
          </ListPageTemplate>
        )}
      </ProductsListPageContent>

      {/* Download Dialog */}
      <Dialog
        open={showDownloadDialog}
        onClose={() => setShowDownloadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {t('download')} {t('products')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <FormControl fullWidth>
              <InputLabel shrink>
                {t('filterByInstance')}
              </InputLabel>
              <Select
                value={selectedInstance}
                onChange={handleInstanceChange}
                label={t('filterByInstance')}
                displayEmpty
                notched
              >
                <MenuItem value="">
                  {t('selectInstance')}
                </MenuItem>
                {instances.map((instance) => (
                  <MenuItem
                    key={instance.id}
                    value={instance.id.toString()}
                  >
                    {instance.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel shrink>
                {t('filterByMarket')}
              </InputLabel>
              <Select
                value={selectedMarket}
                onChange={handleMarketChange}
                label={t('filterByMarket')}
                displayEmpty
                notched
                disabled={!selectedInstance}
              >
                <MenuItem value="">
                  {t('selectMarket')}
                </MenuItem>
                {markets.map((market) => (
                  <MenuItem
                    key={market.customerTypeId}
                    value={market.customerTypeId.toString()}
                  >
                    {market.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            size="lg"
            onClick={() => setShowDownloadDialog(false)}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={() => handleDownload()}
            variant="primary"
            size="lg"
            disabled={!selectedInstance || !selectedMarket || isDownloading}
          >
            {isDownloading ? t('downloading') : t('download')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('upload')} {t('products')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <FormControl fullWidth>
              <InputLabel shrink>
                {t('filterByInstance')}
              </InputLabel>
              <Select
                value={selectedInstance}
                onChange={handleInstanceChange}
                label={t('filterByInstance')}
                displayEmpty
                notched
              >
                <MenuItem value="">
                  {t('selectInstance')}
                </MenuItem>
                {instances.map((instance) => (
                  <MenuItem
                    key={instance.id}
                    value={instance.id.toString()}
                  >
                    {instance.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel shrink>
                {t('filterByMarket')}
              </InputLabel>
              <Select
                value={selectedMarket}
                onChange={handleMarketChange}
                label={t('filterByMarket')}
                displayEmpty
                notched
                disabled={!selectedInstance}
              >
                <MenuItem value="">
                  {t('selectMarket')}
                </MenuItem>
                {markets.map((market) => (
                  <MenuItem
                    key={market.customerTypeId}
                    value={market.customerTypeId.toString()}
                  >
                    {market.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box>
              <Typography sx={{ mb: 2 }}>
                {t('uploadInstructions')}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <input
                  accept=".xlsx,.xls,.xlsm"
                  style={{ display: 'none' }}
                  id="upload-file"
                  type="file"
                  onChange={handleFileChange}
                  aria-label={t('chooseFile')}
                />
                <label htmlFor="upload-file">
                  <Button
                    component="span"
                    size="lg"
                    sx={{ mr: 2 }}
                  >
                    {t('chooseFile')}
                  </Button>
                </label>
                <Typography component="span">
                  {uploadFile ? uploadFile.name : t('noFileSelected')}
                </Typography>
              </Box>
            </Box>
            {uploadResult && (
              <Box sx={{ mt: 2 }}>
                <Typography>{t('uploadResults')}</Typography>
                <Typography>{t('created')}: {uploadResult.created}</Typography>
                <Typography>{t('updated')}: {uploadResult.updated}</Typography>
                {uploadResult.errors && uploadResult.errors.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography color="error">{t('errors')}:</Typography>
                    {uploadResult.errors.map((error, index) => (
                      <Typography
                        key={index}
                        color="error"
                      >
                        {error}
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            size="lg"
            onClick={() => setShowUploadDialog(false)}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleUpload}
            variant="primary"
            size="lg"
            disabled={!uploadFile || !selectedInstance || !selectedMarket || isUploading}
          >
            {isUploading ? t('uploading') : t('upload')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Message */}
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        open={showMessage}
        onClose={handleCloseMessage}
        autoHideDuration={5000}
      >
        <Alert
          onClose={handleCloseMessage}
          severity={messageSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductManagementPage;
